"use client";

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { OrderListItem, OrderService } from "../services/order-service";
import ClothingCategoryService, { ClothingCategory } from "../../clothing-categories/services/clothing-category-service";

const GROUP_COLORS = [
  "bg-indigo-50 border-indigo-200",
  "bg-amber-50 border-amber-200",
  "bg-emerald-50 border-emerald-200",
  "bg-rose-50 border-rose-200",
  "bg-sky-50 border-sky-200",
  "bg-fuchsia-50 border-fuchsia-200",
];

interface ClassificationItem {
  category: string;
  quantity: number;
}

interface ClassificationData {
  items: ClassificationItem[];
  total_items: number;
  note?: string;
}

interface ItemInput {
  category: string;
  quantity: string;
}

interface GroupInput {
  name: string;
  items: ItemInput[];
  weight: string; // kg (ướt) phân bổ cho đơn con — tùy chọn
}

export interface SplitGroup {
  name?: string;
  items: Array<{ category: string; quantity: number }>;
  weight?: number;
  equipment_ids?: number[];
  equipment_codes?: string[];
}

// Dữ liệu 1 TÚI (từ log pickup_check per-túi) để chia theo túi.
interface BagData {
  equipmentId: number;
  code: string;
  items: Array<{ category: string; quantity: number }>;
  total: number;
}

interface SplitOrderModalProps {
  open: boolean;
  order: OrderListItem | null;
  onClose: () => void;
  onSplit: (orderId: number, groups: SplitGroup[], note?: string) => void;
  loading?: boolean;
}

const SplitOrderModal: React.FC<SplitOrderModalProps> = ({
  open,
  order,
  onClose,
  onSplit,
  loading,
}) => {
  const [groups, setGroups] = useState<GroupInput[]>([
    { name: "", items: [{ category: "", quantity: "" }], weight: "" },
    { name: "", items: [{ category: "", quantity: "" }], weight: "" },
  ]);
  const [note, setNote] = useState("");
  const [loadingData, setLoadingData] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);

  // Dữ liệu phân loại từ bước kiểm tra
  const [inspectionData, setInspectionData] = useState<ClassificationData | null>(null);

  // === Chia theo TÚI ===
  const [bags, setBags] = useState<BagData[] | null>(null); // null=chưa load, []=không có túi
  const [bagChild, setBagChild] = useState<number[]>([]);   // bagChild[i] = số đơn con của bags[i] (1-based)
  const [childWeights, setChildWeights] = useState<Record<number, string>>({}); // kg theo số đơn con
  const [itemQty, setItemQty] = useState<Record<string, string>>({}); // override SL: key `${child}:${category}`

  // Suggestion state
  const [activeSuggestion, setActiveSuggestion] = useState<{ groupIndex: number; itemIndex: number } | null>(null);

  // Fetch hooks
  const { fetch: fetchCategories } = ClothingCategoryService.list();
  const { fetch: fetchWorkflowHistory } = OrderService.workflowHistory();

  // Reset data khi mở modal hoặc chuyển đơn mới
  useEffect(() => {
    if (!open || !order) return;
    // Reset tất cả state khi chuyển đơn
    setGroups([
      { name: "", items: [{ category: "", quantity: "" }], weight: "" },
      { name: "", items: [{ category: "", quantity: "" }], weight: "" },
    ]);
    setNote("");
    setInspectionData(null);
    setActiveSuggestion(null);
    setBags(null);
    setBagChild([]);
    setChildWeights({});
    setItemQty({});
  }, [open, order?.id]);

  // Fetch workflow history + categories khi mở modal
  useEffect(() => {
    if (!open || !order) return;
    let mounted = true;

    const load = async () => {
      setLoadingData(true);
      let hasInspection = false;

      // 1) Fetch workflow history để lấy classification_data từ bước INSPECTING
      try {
        const historyRes = await fetchWorkflowHistory(Number(order.id));
        if (mounted && historyRes?.status === 200) {
          const rawData = historyRes.data;
          const logs: any[] = Array.isArray(rawData)
            ? rawData
            : Array.isArray((rawData as any)?.data)
              ? (rawData as any).data
              : Array.isArray((rawData as any)?.list)
                ? (rawData as any).list
                : [];

          // 1a) Gom pickup_check theo TÚI (mỗi túi lấy log mới nhất) để chia theo túi.
          const parseCD = (cd: any) => (typeof cd === 'string' ? (() => { try { return JSON.parse(cd); } catch { return null; } })() : cd);
          const bagMap = new Map<number, { code: string; items: any[]; ts: number }>();
          for (const log of logs) {
            const cd = parseCD(log.classification_data);
            if (cd?.mode !== 'pickup_check') continue;
            const eid = Number(cd?.equipment_id) || 0;
            if (!eid) continue;
            const ts = new Date(log.performed_at || log.created_at || 0).getTime();
            const ex = bagMap.get(eid);
            if (!ex || ts > ex.ts) bagMap.set(eid, { code: cd.equipment_code || String(eid), items: Array.isArray(cd.items) ? cd.items : [], ts });
          }
          const bagList: BagData[] = [...bagMap.entries()].map(([eid, v]) => {
            const items = v.items.map((i: any) => ({ category: String(i.category || ''), quantity: Number(i.quantity) || 0 }));
            return { equipmentId: eid, code: v.code, items, total: items.reduce((s, i) => s + i.quantity, 0) };
          });
          setBags(bagList);
          // Mặc định: mỗi túi = 1 đơn con
          setBagChild(bagList.map((_, i) => i + 1));

          const inspectingLog = logs.find(
            (log: any) => log.classification_data && log.classification_data.items?.length > 0
          );
          if (inspectingLog?.classification_data) {
            const cd = inspectingLog.classification_data as ClassificationData;
            setInspectionData(cd);
            hasInspection = true;

            // Auto-fill: mỗi loại đồ = 1 group (đơn con)
            if (cd.items.length >= 2) {
              setGroups(cd.items.map(item => ({
                name: "",
                items: [{ category: item.category, quantity: String(item.quantity) }],
                weight: "",
              })));
            } else if (cd.items.length === 1) {
              setGroups([
                { name: "", items: [{ category: cd.items[0].category, quantity: String(cd.items[0].quantity) }], weight: "" },
                { name: "", items: [{ category: "", quantity: "" }], weight: "" },
              ]);
            }
          }
        }
      } catch {
        // Ignore
      }

      // 2) Fetch danh sách loại đồ cho autocomplete
      try {
        const res = await fetchCategories({ status: 1 });
        if (!mounted) return;
        if (res?.status === 200) {
          const data = res.data;
          const list: ClothingCategory[] = Array.isArray(data) ? data : (data as any)?.list ?? [];
          if (list.length > 0) {
            setCategoryOptions(list.map(c => c.name));
          }
        }
      } catch {
        // Keep fallback
      }

      if (mounted) setLoadingData(false);
    };

    load();
    return () => { mounted = false; };
  }, [open, order]);

  // === Group handlers ===
  const updateGroupName = (gi: number, value: string) => {
    setGroups(prev => prev.map((g, i) => i === gi ? { ...g, name: value } : g));
  };
  const updateGroupWeight = (gi: number, value: string) => {
    setGroups(prev => prev.map((g, i) => i === gi ? { ...g, weight: value } : g));
  };

  const addGroup = () => {
    setGroups(prev => [...prev, { name: "", items: [{ category: "", quantity: "" }], weight: "" }]);
  };

  const removeGroup = (gi: number) => {
    if (groups.length <= 2) return;
    setGroups(prev => prev.filter((_, i) => i !== gi));
  };

  // === Item handlers ===
  const updateItemCategory = (gi: number, ii: number, value: string) => {
    setGroups(prev => prev.map((g, i) => {
      if (i !== gi) return g;
      const items = g.items.map((item, j) => j === ii ? { ...item, category: value } : item);
      return { ...g, items };
    }));
    setActiveSuggestion(value.trim() ? { groupIndex: gi, itemIndex: ii } : null);
  };

  const updateItemQuantity = (gi: number, ii: number, value: string) => {
    setGroups(prev => prev.map((g, i) => {
      if (i !== gi) return g;
      const items = g.items.map((item, j) => j === ii ? { ...item, quantity: value } : item);
      return { ...g, items };
    }));
  };

  const selectSuggestion = (gi: number, ii: number, value: string) => {
    updateItemCategory(gi, ii, value);
    setActiveSuggestion(null);
  };

  const addItemToGroup = (gi: number) => {
    setGroups(prev => prev.map((g, i) =>
      i === gi ? { ...g, items: [...g.items, { category: "", quantity: "" }] } : g
    ));
  };

  const removeItemFromGroup = (gi: number, ii: number) => {
    const group = groups[gi];
    if (group.items.length <= 1) return;
    setGroups(prev => prev.map((g, i) =>
      i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g
    ));
  };

  // Suggestions filter
  const getSuggestions = useCallback((text: string) => {
    if (!text.trim()) return categoryOptions;
    const lower = text.toLowerCase().trim();
    return categoryOptions.filter(name => name.toLowerCase().includes(lower));
  }, [categoryOptions]);

  // Đơn kiểm đếm bắt buộc nhập SL; đơn thường SL tùy chọn.
  const isCounting = Number((order as any)?.requires_counting) === 1;
  const countingTarget = Number((order as any)?.item_count) || 0;
  const parentPickupWeight = Number((order as any)?.pickup_weight) || 0;

  // Valid groups for API
  const validGroups = useMemo((): SplitGroup[] => {
    return groups
      .map(group => {
        const validItems = group.items
          .filter(item => {
            const qty = parseInt(item.quantity, 10);
            const hasCat = item.category.trim().length > 0;
            return isCounting ? (hasCat && !isNaN(qty) && qty > 0) : hasCat;
          })
          .map(item => {
            const qty = parseInt(item.quantity, 10);
            return { category: item.category.trim(), quantity: isNaN(qty) ? 0 : qty };
          });
        const w = parseFloat(group.weight);
        const hasWeight = !isNaN(w) && w > 0;
        // Đơn kiểm đếm: BẮT BUỘC có loại đồ. Đơn thường: có loại đồ HOẶC số kg (không phân loại thì chỉ ghi kg).
        const valid = isCounting ? validItems.length > 0 : (validItems.length > 0 || hasWeight);
        if (!valid) return null;
        return { items: validItems, weight: hasWeight ? w : undefined };
      })
      .filter(Boolean) as SplitGroup[];
  }, [groups, isCounting]);

  // === Chia theo TÚI: có ≥2 túi thì bật chế độ chia theo túi ===
  const bagMode = !!bags && bags.length >= 2;

  // Đơn con suy ra từ việc gán túi → số đơn con (bagChild). Gộp = nhiều túi cùng số.
  const bagChildren = useMemo(() => {
    if (!bags || bags.length === 0) return [] as Array<{ child: number; bags: BagData[]; items: Array<{ category: string; quantity: number }>; equipmentIds: number[]; equipmentCodes: string[] }>;
    const childNums = [...new Set(bagChild.filter(n => n > 0))].sort((a, b) => a - b);
    return childNums.map(cn => {
      const childBags = bags.filter((_, i) => bagChild[i] === cn);
      const mergedMap = new Map<string, number>();
      for (const b of childBags) for (const it of b.items) mergedMap.set(it.category, (mergedMap.get(it.category) || 0) + it.quantity);
      const items = [...mergedMap.entries()].map(([category, defQty]) => {
        const key = `${cn}:${category}`;
        const q = itemQty[key] !== undefined ? (parseInt(itemQty[key], 10) || 0) : defQty;
        return { category, quantity: q };
      });
      return { child: cn, bags: childBags, items, equipmentIds: childBags.map(b => b.equipmentId), equipmentCodes: childBags.map(b => b.code) };
    });
  }, [bags, bagChild, itemQty]);

  const bagValidGroups = useMemo((): SplitGroup[] => {
    return bagChildren.map(c => ({
      name: `Túi ${c.equipmentCodes.join(', ')}`,
      items: c.items.filter(it => it.category.trim().length > 0 && (isCounting ? it.quantity > 0 : true)),
      weight: (() => { const w = parseFloat(childWeights[c.child] || ''); return !isNaN(w) && w > 0 ? w : undefined; })(),
      equipment_ids: c.equipmentIds,
      equipment_codes: c.equipmentCodes,
    }));
  }, [bagChildren, childWeights, isCounting]);

  // Nhóm hiệu lực dùng để submit + cảnh báo (theo chế độ đang bật).
  const effectiveGroups = bagMode ? bagValidGroups : validGroups;

  // Cảnh báo client (BE vẫn validate lại):
  const splitTotalQty = effectiveGroups.reduce((s, g) => s + g.items.reduce((ss, it) => ss + (it.quantity || 0), 0), 0);
  const splitTotalWeight = effectiveGroups.reduce((s, g) => s + (g.weight || 0), 0);
  const countingMismatch = isCounting && countingTarget > 0 && splitTotalQty !== countingTarget;
  const weightOver = parentPickupWeight > 0 && splitTotalWeight > parentPickupWeight + 1e-6;
  // Bag mode: cần ≥2 đơn con (gán túi vào ≥2 số khác nhau).
  const canSubmit = effectiveGroups.length >= 2 && !countingMismatch && !weightOver;

  // Totals
  const totalItems = groups.reduce((sum, g) =>
    sum + g.items.reduce((s, item) => {
      const qty = parseInt(item.quantity, 10);
      return s + (isNaN(qty) ? 0 : qty);
    }, 0)
  , 0);

  const getGroupItemCount = (group: GroupInput) =>
    group.items.reduce((sum, item) => {
      const qty = parseInt(item.quantity, 10);
      return sum + (isNaN(qty) ? 0 : qty);
    }, 0);

  const inspectionTotal = inspectionData?.total_items ?? 0;

  // === Handlers chia theo túi ===
  const setBagToChild = (bagIdx: number, child: number) => {
    setBagChild(prev => prev.map((c, i) => (i === bagIdx ? child : c)));
  };
  const maxChild = bagChild.length > 0 ? Math.max(...bagChild, 0) : 0;
  const updateChildWeight = (child: number, value: string) => {
    setChildWeights(prev => ({ ...prev, [child]: value }));
  };
  const updateChildItemQty = (child: number, category: string, value: string) => {
    setItemQty(prev => ({ ...prev, [`${child}:${category}`]: value }));
  };

  const handleSplit = () => {
    if (!order?.id || !canSubmit) return;
    onSplit(order.id, effectiveGroups, note || undefined);
  };

  const handleClose = () => {
    setGroups([
      { name: "", items: [{ category: "", quantity: "" }], weight: "" },
      { name: "", items: [{ category: "", quantity: "" }], weight: "" },
    ]);
    setNote("");
    setInspectionData(null);
    setActiveSuggestion(null);
    onClose();
  };

  if (!open || !order) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[600px] max-w-[95vw] max-h-[90vh] overflow-y-auto p-6 shadow-2xl relative">
        <button
          onClick={handleClose}
          disabled={loading}
          title="Đóng"
          className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition disabled:opacity-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h3 className="text-lg font-semibold mb-1 text-center text-indigo-600">
          Chia đơn hàng
        </h3>
        <p className="text-xs text-center text-slate-500 mb-4">
          Đơn: <b>{order.code ?? `OD-${order.id}`}</b>
        </p>

        {/* Kết quả kiểm tra */}
        {inspectionData && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-emerald-600">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-semibold text-emerald-700">Kết quả kiểm tra</span>
              <span className="ml-auto text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                {inspectionTotal} cái
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {inspectionData.items.map((item, idx) => (
                <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-md text-xs border border-emerald-200">
                  <span className="text-slate-600">{item.category}:</span>
                  <span className="font-semibold text-emerald-700">{item.quantity}</span>
                </span>
              ))}
            </div>
            {inspectionData.note && (
              <p className="text-xs text-emerald-600 mt-2 italic">Ghi chú KT: {inspectionData.note}</p>
            )}
          </div>
        )}

        {/* Info */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
          <p className="text-xs text-indigo-700">
            {bagMode
              ? "Chia theo TÚI: mỗi túi mặc định thành 1 đơn con. Đổi số 'Đơn con' để gộp nhiều túi vào cùng 1 đơn con. Túi sẽ được chuyển sang đơn con tương ứng."
              : inspectionData
                ? "Đã tự động tạo đơn con từ kết quả kiểm tra. Bạn có thể gom nhiều loại đồ vào cùng 1 đơn con, hoặc điều chỉnh số lượng."
                : "Mỗi card là 1 đơn con, có thể chứa nhiều loại đồ. Cần ít nhất 2 đơn con để chia đơn."
            }
          </p>
        </div>

        {loadingData ? (
          <div className="flex items-center justify-center gap-2 py-8">
            <svg className="animate-spin w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-sm text-slate-500">Đang tải dữ liệu...</span>
          </div>
        ) : bagMode ? (
          <>
            {/* Gán túi → đơn con */}
            <div className="space-y-2 mb-4">
              <p className="text-xs font-semibold text-slate-600">Gán túi vào đơn con (đổi số để gộp)</p>
              {(bags || []).map((bag, bi) => (
                <div key={bag.equipmentId} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-cyan-600">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007z" />
                      </svg>
                      <span className="text-sm font-semibold text-slate-700">Túi {bag.code}</span>
                      <span className="text-xs text-cyan-600 font-semibold">{bag.total} cái</span>
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {bag.items.map(it => `${it.category}(${it.quantity})`).join(', ') || 'Không nhập loại đồ'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-xs text-slate-500">Đơn con</span>
                    <input
                      type="number"
                      min={1}
                      value={bagChild[bi] || 1}
                      onChange={e => setBagToChild(bi, Math.max(1, parseInt(e.target.value, 10) || 1))}
                      className="w-14 px-2 py-1 border rounded-lg text-center text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none bg-white"
                    />
                  </div>
                </div>
              ))}
              {maxChild > 0 && (
                <p className="text-[11px] text-slate-400">Mẹo: đặt 2 túi cùng số → gộp vào 1 đơn con. Số lớn hơn ({maxChild + 1}) → tạo đơn con mới.</p>
              )}
            </div>

            {/* Preview đơn con */}
            <div className="space-y-3 mb-4">
              {bagChildren.map((c, ci) => {
                const childTotal = c.items.reduce((s, it) => s + it.quantity, 0);
                return (
                  <div key={c.child} className={`rounded-xl border p-4 ${GROUP_COLORS[ci % GROUP_COLORS.length]}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">{ci + 1}</span>
                      <span className="text-sm font-semibold text-slate-700 flex-1 truncate">Đơn con {c.child} · Túi {c.equipmentCodes.join(', ')}</span>
                      {childTotal > 0 && (
                        <span className="text-xs font-semibold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full shrink-0">{childTotal} cái</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-slate-500 flex-1">Số kg đơn con</span>
                      <input
                        type="number" min="0" step="0.1"
                        className="w-28 px-3 py-1.5 border rounded-lg text-sm text-center focus:ring-2 focus:ring-indigo-300 focus:outline-none bg-white"
                        placeholder="kg"
                        value={childWeights[c.child] || ''}
                        onChange={e => updateChildWeight(c.child, e.target.value)}
                      />
                      <span className="text-xs text-slate-400">kg</span>
                    </div>
                    <div className="space-y-1.5">
                      {c.items.length === 0 ? (
                        <p className="text-xs italic text-slate-400">Không có loại đồ (chia theo túi)</p>
                      ) : (
                        c.items.map((it, ii) => (
                          <div key={ii} className="flex items-center gap-2">
                            <span className="flex-1 text-sm text-slate-700">{it.category}</span>
                            <input
                              type="number" min="0"
                              className="w-20 px-3 py-1.5 border rounded-lg text-center text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none bg-white"
                              value={String(it.quantity)}
                              onChange={e => updateChildItemQty(c.child, it.category, e.target.value)}
                            />
                            <span className="text-xs text-slate-400 w-6">cái</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Groups */}
            <div className="space-y-4 mb-4">
              {groups.map((group, gi) => {
                const groupCount = getGroupItemCount(group);
                return (
                  <div key={gi} className={`rounded-xl border p-4 ${GROUP_COLORS[gi % GROUP_COLORS.length]}`}>
                    {/* Group header */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
                        {gi + 1}
                      </span>
                      <span className="text-sm font-semibold text-slate-700 flex-1">Đơn con {gi + 1}</span>
                      {groupCount > 0 && (
                        <span className="text-xs font-semibold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                          {groupCount} cái
                        </span>
                      )}
                      {groups.length > 2 && (
                        <button
                          onClick={() => removeGroup(gi)}
                          className="text-red-400 hover:text-red-600 transition p-1"
                          title="Xoá đơn con"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Số kg đơn con (phân bổ; giá tính bằng cân final ở cuối) */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs text-slate-500 flex-1">Số kg đơn con</span>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        className="w-28 px-3 py-1.5 border rounded-lg text-sm text-center focus:ring-2 focus:ring-indigo-300 focus:outline-none bg-white"
                        placeholder="kg"
                        value={group.weight}
                        onChange={e => updateGroupWeight(gi, e.target.value)}
                      />
                      <span className="text-xs text-slate-400">kg</span>
                    </div>

                    {/* Items */}
                    <div className="space-y-2">
                      {group.items.map((item, ii) => {
                        const showSugg = activeSuggestion?.groupIndex === gi && activeSuggestion?.itemIndex === ii;
                        const suggestions = showSugg ? getSuggestions(item.category) : [];
                        const inspectedQty = inspectionData?.items.find(
                          i => i.category.toLowerCase() === item.category.toLowerCase().trim()
                        )?.quantity;

                        return (
                          <div key={ii} className="relative">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 relative">
                                <input
                                  type="text"
                                  className="w-full px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none bg-white"
                                  placeholder="Tên loại đồ"
                                  value={item.category}
                                  onChange={e => updateItemCategory(gi, ii, e.target.value)}
                                  onFocus={() => item.category.trim() && setActiveSuggestion({ groupIndex: gi, itemIndex: ii })}
                                  onBlur={() => setTimeout(() => setActiveSuggestion(null), 200)}
                                />
                                {/* Suggestions dropdown */}
                                {showSugg && suggestions.length > 0 && (
                                  <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                                    {suggestions.map((s, si) => (
                                      <button
                                        key={si}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition"
                                        onMouseDown={() => selectSuggestion(gi, ii, s)}
                                      >
                                        {s}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <input
                                type="number"
                                min="0"
                                className="w-20 px-3 py-1.5 border rounded-lg text-center text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none bg-white"
                                placeholder="0"
                                value={item.quantity}
                                onChange={e => updateItemQuantity(gi, ii, e.target.value)}
                              />
                              <span className="text-xs text-slate-400 w-6">cái</span>
                              {inspectedQty !== undefined && (
                                <span className="text-xs text-emerald-500 whitespace-nowrap">(KT:{inspectedQty})</span>
                              )}
                              {group.items.length > 1 && (
                                <button
                                  onClick={() => removeItemFromGroup(gi, ii)}
                                  className="text-slate-300 hover:text-red-400 transition"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Add item */}
                    <button
                      onClick={() => addItemToGroup(gi)}
                      className="mt-2 w-full py-1.5 border border-dashed border-indigo-300 rounded-lg text-xs text-indigo-500 hover:bg-white transition flex items-center justify-center gap-1"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Thêm loại đồ
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Add group */}
            <button
              onClick={addGroup}
              className="w-full py-2.5 border-2 border-dashed border-indigo-400 rounded-xl text-sm text-indigo-600 font-medium hover:bg-indigo-50 transition flex items-center justify-center gap-2 mb-4"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Thêm đơn con
            </button>
          </>
        )}

        {/* Total */}
        <div className="flex justify-between items-center bg-slate-50 rounded-lg px-4 py-2 mb-2">
          <span className="text-sm font-medium text-slate-700">Tổng cộng</span>
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold text-indigo-600">{bagMode ? splitTotalQty : totalItems} cái</span>
            {isCounting && countingTarget > 0 && (
              <span className={`text-xs ${countingMismatch ? "text-red-500 font-semibold" : "text-emerald-600"}`}>/ {countingTarget} (kiểm đếm)</span>
            )}
            {parentPickupWeight > 0 && (
              <span className={`text-xs ${weightOver ? "text-red-500 font-semibold" : "text-slate-500"}`}>{splitTotalWeight.toFixed(1)}/{parentPickupWeight} kg</span>
            )}
          </div>
        </div>

        {/* Cảnh báo */}
        {(countingMismatch || weightOver) && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4 text-xs text-red-700 space-y-0.5">
            {countingMismatch && <div>• Đơn kiểm đếm: tổng số lượng chia (<b>{splitTotalQty}</b>) phải khớp số kiểm đếm nhận (<b>{countingTarget}</b> cái).</div>}
            {weightOver && <div>• Tổng kg chia (<b>{splitTotalWeight.toFixed(1)}</b>) vượt quá số cân khi nhận (<b>{parentPickupWeight}</b> kg).</div>}
          </div>
        )}

        {/* Note */}
        <div className="mb-4">
          <label className="text-sm font-medium text-slate-700 block mb-1">Ghi chú</label>
          <textarea
            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none resize-none"
            rows={2}
            placeholder="Ghi chú thêm (VD: Đồ trắng cần tẩy riêng)"
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition disabled:opacity-50"
          >
            Hủy
          </button>
          <button
            onClick={handleSplit}
            disabled={loading || !canSubmit}
            className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && (
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            Chia đơn ({effectiveGroups.length} đơn con)
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitOrderModal;
